import React, { ReactNode, cloneElement } from 'react';
import classNames from 'classnames';
import {
  offset,
  shift,
  autoUpdate,
  useFloating,
  useInteractions,
  useRole,
  useDismiss,
  useClick,
  hide as hideMiddleware,
} from '@floating-ui/react';

const getPlacement = (placement: string) => {
  switch (placement) {
    case 'bottom-right':
      return 'top-end';
    case 'top-right':
      return 'bottom-end';
    case 'bottom-left':
      return 'top-start';
    case 'top-left':
      return 'bottom-start';
    default:
      return 'bottom-end';
  }
};
export interface FloatingModalContainerProps {
  /** @default 'bottom-right' */
  position?: 'bottom-right' | 'top-right' | 'bottom-left' | 'top-left';
  /** @default false */
  canClickOutside?: boolean;
  /** @default false */
  canCloseEscapeKey?: boolean;
  /** @default 'click' */
  openerTriggerEvent?: 'click' | 'none';
  className?: string;
  children: ReactNode;
  trigger: ReactNode;
  opened?: boolean;
  setOpened: (opened: boolean) => void;
}

export const FloatingModalContainer: React.FC<FloatingModalContainerProps> = ({
  position = 'bottom-right',
  canClickOutside = false,
  canCloseEscapeKey = false,
  openerTriggerEvent = 'click',
  className,
  children,
  trigger,
  opened,
  setOpened,
}) => {
  const {
    strategy,
    x,
    y,
    refs,
    context,
    middlewareData: { hide },
  } = useFloating({
    open: opened,
    placement: getPlacement(position),
    onOpenChange: setOpened,
    whileElementsMounted: autoUpdate,
    middleware: [offset(8), shift(), hideMiddleware()],
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useClick(context, { enabled: openerTriggerEvent === 'click' }),
    useRole(context),
    useDismiss(context, { escapeKey: canCloseEscapeKey, outsidePress: canClickOutside }),
  ]);

  const renderToFloatingModalBase = () => {
    return (
      <div
        {...getFloatingProps({
          ref: refs.setFloating,
          className: classNames('pds-floating-modal', className),
          style: {
            position: strategy,
            top: y ?? '',
            left: x ?? '',
            display: hide?.referenceHidden ? 'none' : 'block',
            pointerEvents: opened ? 'auto' : 'none',
          },
        })}
      >
        {cloneElement(children as React.ReactElement, { ...(children as React.ReactElement).props, opened })}
      </div>
    );
  };

  return (
    <>
      {cloneElement(trigger as React.ReactElement, {
        ...getReferenceProps({
          ref: refs.setReference,
          ...(trigger as React.ReactElement).props,
          onClick: (e: React.MouseEvent) => {
            e.stopPropagation();
            const childrenProps = (trigger as React.ReactElement).props;
            childrenProps.onClick && (childrenProps as Record<'onClick', React.MouseEventHandler>).onClick(e);
          },
        }),
        opened,
      })}
      {renderToFloatingModalBase()}
    </>
  );
};
